// PeopleTab — contact tracker (صلة الرحم) ported from Apps/Yawmi/PeopleHub.ts.
// Storage: aweni_yawmi_contacts + aweni_yawmi_messages.
// BOUNDARY: secular contact management. The Arabic subtitle "صلة الرحم" is an i18n
// string for framing; NO Quran/hadith text inline. WhatsApp message templates are
// generic occasion messages (birthday, eid, condolence) — no religious quotations.
// Birthday cross-link: writes opt-in birthdays to aweni_yawmi_followed_birthdays
// for the Yawmi calendar (T1) to surface in its events column.
import type { AweniContext } from '../../types';

type Relationship = 'parent' | 'spouse' | 'child' | 'sibling' | 'grandparent'
  | 'uncle_aunt' | 'cousin' | 'friend' | 'colleague' | 'neighbor' | 'teacher' | 'other';

interface Contact {
  id: string;
  name: string;
  phone: string;
  relationship: Relationship;
  birthday?: string;            // MM-DD (preferred — no year for privacy)
  showInCalendar?: boolean;     // opt-in cross-link to calendar T1
  contactGoalDays: number;      // overdue threshold
  lastContactedAt?: string;     // ISO
  notes: string;
}

interface MessageLog {
  id: string;
  contactId: string;
  occasion: string;
  channel: 'whatsapp' | 'sms' | 'call' | 'visit' | 'other';
  sentAt: string;
}

const CONTACTS_KEY = 'aweni_yawmi_contacts';
const MESSAGES_KEY = 'aweni_yawmi_messages';
const BIRTHDAYS_KEY = 'aweni_yawmi_followed_birthdays';
const LEGACY_CONTACTS = 'yw_contacts';
const LEGACY_MESSAGES = 'yw_messages';
const MIGRATION_FLAG = 'aweni_yawmi_yw_migration_done';

const REL_ICONS: Record<Relationship, string> = {
  parent: '👨‍👩‍👦', spouse: '💑', child: '👶', sibling: '👫',
  grandparent: '👴', uncle_aunt: '👨‍👧', cousin: '🤝', friend: '🫂',
  colleague: '💼', neighbor: '🏠', teacher: '📚', other: '👤',
};

const RELATIONSHIPS: Relationship[] = [
  'parent','spouse','child','sibling','grandparent','uncle_aunt',
  'cousin','friend','colleague','neighbor','teacher','other',
];

export class PeopleTab {
  private el: HTMLElement;
  private contacts: Contact[] = [];
  private messages: MessageLog[] = [];
  private editing: Contact | null = null;
  private adding = false;
  private filter: 'all' | 'overdue' | 'birthdays-soon' = 'all';

  constructor(container: HTMLElement) { this.el = container; }

  mount(ctx: AweniContext): void {
    this.migrate();
    try { this.contacts = JSON.parse(localStorage.getItem(CONTACTS_KEY) || '[]'); } catch { this.contacts = []; }
    try { this.messages = JSON.parse(localStorage.getItem(MESSAGES_KEY) || '[]'); } catch { this.messages = []; }
    this.syncBirthdayCalendar();
    this.render(ctx);
  }

  unmount(): void { this.el.innerHTML = ''; }

  private migrate(): void {
    const flag = localStorage.getItem(MIGRATION_FLAG) || '';
    if (flag.includes('contacts')) return;
    try {
      const c = localStorage.getItem(LEGACY_CONTACTS);
      const m = localStorage.getItem(LEGACY_MESSAGES);
      if (c && !localStorage.getItem(CONTACTS_KEY)) localStorage.setItem(CONTACTS_KEY, c);
      if (m && !localStorage.getItem(MESSAGES_KEY)) localStorage.setItem(MESSAGES_KEY, m);
    } catch { /* best-effort */ }
    localStorage.setItem(MIGRATION_FLAG, (flag ? flag + ',' : '') + 'contacts');
  }

  private save(): void {
    localStorage.setItem(CONTACTS_KEY, JSON.stringify(this.contacts));
    localStorage.setItem(MESSAGES_KEY, JSON.stringify(this.messages));
    this.syncBirthdayCalendar();
  }

  /** Push opt-in birthdays to the calendar (T1's followed_birthdays store). */
  private syncBirthdayCalendar(): void {
    const opted = this.contacts
      .filter(c => c.birthday && c.showInCalendar)
      .map(c => {
        const md = c.birthday!.length >= 5 ? c.birthday!.slice(-5) : c.birthday!;
        return { name: c.name, date: md };
      });
    try { localStorage.setItem(BIRTHDAYS_KEY, JSON.stringify(opted)); } catch { /* quota */ }
  }

  private daysSince(iso?: string): number {
    if (!iso) return Infinity;
    return Math.floor((Date.now() - new Date(iso).getTime()) / 86400_000);
  }

  private isOverdue(c: Contact): boolean {
    return this.daysSince(c.lastContactedAt) > c.contactGoalDays;
  }

  private daysUntilBirthday(c: Contact): number {
    if (!c.birthday) return Infinity;
    const md = c.birthday.length >= 5 ? c.birthday.slice(-5) : c.birthday;
    const [mo, da] = md.split('-').map(n => parseInt(n, 10));
    if (!mo || !da) return Infinity;
    const now = new Date();
    let next = new Date(now.getFullYear(), mo - 1, da);
    if (next < now) next = new Date(now.getFullYear() + 1, mo - 1, da);
    return Math.ceil((next.getTime() - now.getTime()) / 86400_000);
  }

  private logContact(contactId: string, channel: MessageLog['channel'], occasion = 'check_in'): void {
    const c = this.contacts.find(x => x.id === contactId);
    if (!c) return;
    c.lastContactedAt = new Date().toISOString();
    this.messages.unshift({
      id: 'm_' + Math.random().toString(36).slice(2, 10),
      contactId, occasion, channel, sentAt: c.lastContactedAt,
    });
    // Trim to 200 most recent
    if (this.messages.length > 200) this.messages = this.messages.slice(0, 200);
    this.save();
  }

  private openWhatsApp(c: Contact, occasion: string, ctx: AweniContext): void {
    const phone = (c.phone || '').replace(/\D/g, '');
    if (!phone) { alert(ctx.i18n.t('yawmi_people_no_phone') || 'No phone number set.'); return; }
    const t = (k: string, fb: string) => ctx.i18n.t(k) || fb;
    // Generic, non-religious templates
    const templates: Record<string, string> = {
      birthday:  t('yawmi_people_msg_birthday',  `Happy birthday ${c.name}! 🎂`),
      eid:       t('yawmi_people_msg_eid',       `Eid Mubarak ${c.name}! 🌙`),
      ramadan:   t('yawmi_people_msg_ramadan',   `Ramadan Kareem ${c.name}!`),
      condolence:t('yawmi_people_msg_condolence',`Thinking of you ${c.name}. May you find strength.`),
      get_well:  t('yawmi_people_msg_get_well',  `Get well soon ${c.name}. ❤️`),
      check_in:  t('yawmi_people_msg_check_in',  `Hi ${c.name}, just checking in. How are you?`),
    };
    const msg = templates[occasion] || templates.check_in;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank', 'noopener');
    this.logContact(c.id, 'whatsapp', occasion);
  }

  private save_(c: Partial<Contact>, ctx: AweniContext): void {
    if (!c.name || !c.name.trim()) {
      alert(ctx.i18n.t('yawmi_people_name_required') || 'Name required.');
      return;
    }
    if (this.editing) {
      Object.assign(this.editing, c);
    } else {
      this.contacts.unshift({
        id: 'c_' + Math.random().toString(36).slice(2, 10),
        name: c.name.trim(),
        phone: (c.phone || '').trim(),
        relationship: c.relationship || 'other',
        birthday: c.birthday || undefined,
        showInCalendar: c.showInCalendar || false,
        contactGoalDays: c.contactGoalDays || 30,
        notes: (c.notes || '').trim(),
      });
    }
    this.editing = null;
    this.adding = false;
    this.save();
  }

  private deleteContact(id: string): void {
    this.contacts = this.contacts.filter(c => c.id !== id);
    this.messages = this.messages.filter(m => m.contactId !== id);
    this.save();
  }

  private filtered(): Contact[] {
    let out = this.contacts;
    if (this.filter === 'overdue') out = out.filter(c => this.isOverdue(c));
    else if (this.filter === 'birthdays-soon') out = out.filter(c => this.daysUntilBirthday(c) <= 30);
    return [...out].sort((a, b) => a.name.localeCompare(b.name));
  }

  private render(ctx: AweniContext): void {
    const t = (k: string, fb: string) => ctx.i18n.t(k) || fb;
    const items = this.filtered();
    const overdueCount = this.contacts.filter(c => this.isOverdue(c)).length;
    const bdayCount = this.contacts.filter(c => this.daysUntilBirthday(c) <= 30).length;

    if (this.adding || this.editing) {
      return this.renderForm(ctx);
    }

    this.el.innerHTML = `
      <style>
        .pt-subtitle{font-size:.75rem;color:var(--ln-text-secondary);text-align:center;margin-bottom:8px;font-style:italic}
        .pt-stats{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-bottom:12px}
        .pt-stat{background:var(--ln-bg-card,#fff);border:1px solid var(--ln-border,rgba(0,0,0,.08));
          border-radius:var(--ln-radius-md,12px);padding:10px;text-align:center;cursor:pointer}
        .pt-stat--alert{border-color:#f59e0b;background:rgba(245,158,11,.08)}
        .pt-stat-n{font-size:1.3rem;font-weight:800;color:var(--ln-accent,#0A0A0A)}
        .pt-stat-l{font-size:.7rem;color:var(--ln-text-secondary);text-transform:uppercase;letter-spacing:.4px}
        .pt-filters{display:flex;gap:6px;margin-bottom:12px;overflow-x:auto;scrollbar-width:none}
        .pt-filters::-webkit-scrollbar{display:none}
        .pt-filter{flex-shrink:0;padding:6px 12px;border:1.5px solid var(--ln-border);border-radius:var(--ln-radius-full,9999px);
          background:var(--ln-bg-card,#fff);color:var(--ln-text-secondary);font-size:.8rem;font-weight:700;cursor:pointer;white-space:nowrap}
        .pt-filter--active{background:var(--ln-accent);color:var(--ln-accent-text);border-color:var(--ln-accent)}
        .pt-add{width:100%;padding:10px;margin-bottom:12px;background:var(--ln-accent);color:var(--ln-accent-text);
          border:none;border-radius:var(--ln-radius-md,12px);font-weight:700;cursor:pointer}
        .pt-list{display:flex;flex-direction:column;gap:8px}
        .pt-card{background:var(--ln-bg-card,#fff);border:1px solid var(--ln-border,rgba(0,0,0,.08));
          border-radius:var(--ln-radius-md,12px);padding:10px 12px;position:relative}
        .pt-card--overdue{border-left:3px solid #f59e0b}
        .pt-row{display:flex;align-items:center;gap:10px}
        .pt-ic{font-size:1.6rem}
        .pt-info{flex:1;min-width:0}
        .pt-name{font-weight:700;font-size:.95rem;color:var(--ln-text-primary,#222);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
        .pt-meta{font-size:.7rem;color:var(--ln-text-secondary);margin-top:2px}
        .pt-actions{margin-top:10px;display:flex;flex-wrap:wrap;gap:6px}
        .pt-btn{padding:5px 10px;background:transparent;border:1px solid var(--ln-border);
          border-radius:var(--ln-radius-sm,6px);font-size:.72rem;color:var(--ln-text-secondary);cursor:pointer}
        .pt-btn--wa{background:#25D366;color:#fff;border-color:#25D366}
        .pt-btn--danger{color:#b91c1c}
        .pt-empty{text-align:center;padding:24px;color:var(--ln-text-secondary);font-size:.9rem}
      </style>

      <div class="pt-subtitle">${escHtml(t('yawmi_people_subtitle','صلة الرحم — kinship'))}</div>

      <div class="pt-stats">
        <div class="pt-stat" data-filter="all">
          <div class="pt-stat-n">${this.contacts.length}</div>
          <div class="pt-stat-l">${escHtml(t('yawmi_people_total','Contacts'))}</div>
        </div>
        <div class="pt-stat${overdueCount>0?' pt-stat--alert':''}" data-filter="overdue">
          <div class="pt-stat-n">${overdueCount}</div>
          <div class="pt-stat-l">${escHtml(t('yawmi_people_overdue','Overdue'))}</div>
        </div>
        <div class="pt-stat" data-filter="birthdays-soon">
          <div class="pt-stat-n">${bdayCount}</div>
          <div class="pt-stat-l">${escHtml(t('yawmi_people_bdays_soon','Birthdays'))}</div>
        </div>
      </div>

      <div class="pt-filters">
        ${(['all','overdue','birthdays-soon'] as const).map(f => `
          <button class="pt-filter${f===this.filter?' pt-filter--active':''}" data-filter="${f}">
            ${escHtml(t('yawmi_people_filter_'+f.replace('-','_'), f))}
          </button>`).join('')}
      </div>

      <button class="pt-add" id="pt-add-btn">+ ${escHtml(t('yawmi_people_add','Add contact'))}</button>

      <div class="pt-list">
        ${items.length === 0
          ? `<div class="pt-empty">${escHtml(t('yawmi_people_empty','No contacts yet.'))}</div>`
          : items.map(c => {
              const days = this.daysSince(c.lastContactedAt);
              const lastTxt = days === Infinity
                ? t('yawmi_people_never_contacted','Never contacted')
                : days === 0 ? t('yawmi_people_today','Today')
                : days === 1 ? t('yawmi_people_yesterday','Yesterday')
                : (t('yawmi_people_days_ago','{n}d ago')).replace('{n}', String(days));
              const bdayInfo = c.birthday
                ? (() => {
                    const dd = this.daysUntilBirthday(c);
                    return dd <= 30 ? ` · 🎂 ${(t('yawmi_people_bday_in','in {n}d')).replace('{n}', String(dd))}` : '';
                  })()
                : '';
              return `
                <div class="pt-card${this.isOverdue(c)?' pt-card--overdue':''}" role="listitem">
                  <div class="pt-row">
                    <span class="pt-ic" aria-hidden="true">${REL_ICONS[c.relationship]}</span>
                    <div class="pt-info">
                      <div class="pt-name">${escHtml(c.name)}</div>
                      <div class="pt-meta">${escHtml(t('yawmi_people_rel_'+c.relationship, c.relationship))} · ${escHtml(lastTxt)}${bdayInfo}</div>
                    </div>
                  </div>
                  <div class="pt-actions">
                    ${c.phone ? `
                      <button class="pt-btn pt-btn--wa" data-act="wa" data-id="${c.id}" data-occ="check_in">💬 ${escHtml(t('yawmi_people_check_in','Check in'))}</button>
                      <button class="pt-btn" data-act="call" data-id="${c.id}">📞 ${escHtml(t('yawmi_people_call','Call'))}</button>
                    ` : ''}
                    <button class="pt-btn" data-act="edit" data-id="${c.id}">${escHtml(t('yawmi_people_edit','Edit'))}</button>
                    <button class="pt-btn pt-btn--danger" data-act="del" data-id="${c.id}">${escHtml(t('yawmi_people_delete','Delete'))}</button>
                  </div>
                </div>`;
            }).join('')}
      </div>`;

    // Stat tiles double as filter shortcuts
    this.el.querySelectorAll<HTMLElement>('.pt-stat').forEach(s => {
      s.addEventListener('click', () => {
        this.filter = (s.dataset.filter || 'all') as typeof this.filter;
        this.render(ctx);
      });
    });
    this.el.querySelectorAll<HTMLButtonElement>('.pt-filter').forEach(b => {
      b.addEventListener('click', () => {
        this.filter = b.dataset.filter as typeof this.filter;
        this.render(ctx);
      });
    });
    this.el.querySelector('#pt-add-btn')?.addEventListener('click', () => {
      this.adding = true; this.render(ctx);
    });
    this.el.querySelectorAll<HTMLButtonElement>('.pt-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = btn.dataset.id!;
        const c = this.contacts.find(x => x.id === id);
        if (!c) return;
        const act = btn.dataset.act;
        if (act === 'wa') {
          this.openWhatsApp(c, btn.dataset.occ || 'check_in', ctx);
          this.render(ctx);
        } else if (act === 'call') {
          this.logContact(id, 'call');
          if (c.phone) window.location.href = `tel:${c.phone}`;
          this.render(ctx);
        } else if (act === 'edit') {
          this.editing = c; this.render(ctx);
        } else if (act === 'del') {
          if (confirm(t('yawmi_people_confirm_delete','Delete this contact?'))) {
            this.deleteContact(id); this.render(ctx);
          }
        }
      });
    });
  }

  private renderForm(ctx: AweniContext): void {
    const t = (k: string, fb: string) => ctx.i18n.t(k) || fb;
    const c = this.editing || ({} as Partial<Contact>);

    this.el.innerHTML = `
      <style>
        .pt-form{background:var(--ln-bg-card,#fff);border:1px solid var(--ln-border,rgba(0,0,0,.1));
          border-radius:var(--ln-radius-md,12px);padding:12px;display:flex;flex-direction:column;gap:8px}
        .pt-form label{font-size:.75rem;font-weight:700;color:var(--ln-text-secondary);text-transform:uppercase;letter-spacing:.4px}
        .pt-form input, .pt-form select, .pt-form textarea{
          width:100%;padding:8px;border:1px solid var(--ln-border);border-radius:var(--ln-radius-sm,6px);
          font-family:inherit;font-size:.9rem;background:var(--ln-bg-card,#fff)}
        .pt-form-row{display:flex;gap:8px}
        .pt-form-row > *{flex:1}
        .pt-form-actions{display:flex;gap:8px;justify-content:flex-end;margin-top:4px}
        .pt-action{padding:8px 16px;border:none;border-radius:var(--ln-radius-sm,6px);cursor:pointer;font-weight:700}
        .pt-action--primary{background:var(--ln-accent);color:var(--ln-accent-text)}
        .pt-action--secondary{background:transparent;color:var(--ln-text-secondary);border:1px solid var(--ln-border)}
        .pt-form-check{display:flex;align-items:center;gap:6px;font-size:.85rem;color:var(--ln-text-primary)}
      </style>
      <form class="pt-form" id="pt-form">
        <div>
          <label>${escHtml(t('yawmi_people_name','Name'))}*</label>
          <input id="pt-name" type="text" value="${escAttr(c.name || '')}" required maxlength="80">
        </div>
        <div class="pt-form-row">
          <div>
            <label>${escHtml(t('yawmi_people_phone','Phone'))}</label>
            <input id="pt-phone" type="tel" value="${escAttr(c.phone || '')}" maxlength="20">
          </div>
          <div>
            <label>${escHtml(t('yawmi_people_relationship','Relationship'))}</label>
            <select id="pt-rel">
              ${RELATIONSHIPS.map(r => `<option value="${r}"${c.relationship===r?' selected':''}>${REL_ICONS[r]} ${escHtml(t('yawmi_people_rel_'+r, r))}</option>`).join('')}
            </select>
          </div>
        </div>
        <div class="pt-form-row">
          <div>
            <label>${escHtml(t('yawmi_people_birthday','Birthday (MM-DD)'))}</label>
            <input id="pt-bday" type="text" placeholder="MM-DD" value="${escAttr(c.birthday || '')}" maxlength="10">
          </div>
          <div>
            <label>${escHtml(t('yawmi_people_goal_days','Contact every (days)'))}</label>
            <input id="pt-goal" type="number" min="1" max="365" value="${c.contactGoalDays || 30}">
          </div>
        </div>
        <label class="pt-form-check">
          <input id="pt-show-cal" type="checkbox"${c.showInCalendar ? ' checked' : ''}>
          ${escHtml(t('yawmi_people_show_calendar','Show birthday in calendar (no year, MM-DD only)'))}
        </label>
        <div>
          <label>${escHtml(t('yawmi_people_notes','Notes'))}</label>
          <textarea id="pt-notes" rows="2">${escHtml(c.notes || '')}</textarea>
        </div>
        <div class="pt-form-actions">
          <button type="button" class="pt-action pt-action--secondary" id="pt-cancel">${escHtml(t('yawmi_people_cancel','Cancel'))}</button>
          <button type="submit" class="pt-action pt-action--primary">${escHtml(t('yawmi_people_save','Save'))}</button>
        </div>
      </form>`;

    const formEl = this.el.querySelector<HTMLFormElement>('#pt-form')!;
    formEl.addEventListener('submit', (ev) => {
      ev.preventDefault();
      const get = (id: string) => (formEl.querySelector('#'+id) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement)?.value || '';
      const bday = get('pt-bday').trim();
      this.save_({
        name: get('pt-name'),
        phone: get('pt-phone'),
        relationship: (get('pt-rel') || 'other') as Relationship,
        birthday: bday || undefined,
        showInCalendar: (formEl.querySelector('#pt-show-cal') as HTMLInputElement).checked,
        contactGoalDays: parseInt(get('pt-goal') || '30', 10),
        notes: get('pt-notes'),
      }, ctx);
      this.render(ctx);
    });
    this.el.querySelector('#pt-cancel')?.addEventListener('click', () => {
      this.editing = null; this.adding = false; this.render(ctx);
    });
  }
}

function escHtml(s: string | undefined | null): string {
  if (s == null) return '';
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function escAttr(s: string | undefined | null): string {
  if (s == null) return '';
  return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}
